import React, { useState } from "react";
import DateSelector from "../../../components/shared/fields/DateSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import ColdStorageKg from "../../../images/material_orange.svg";
import KgEntered from "../../../images/material_kg.svg";
import Datatable from "../../../components/shared/datatable/Datatable";
import Button from "../../../components/shared/buttons/button";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import PastingSortingModal from "./PastingSortingModal";
import { useGetPastingSortingQuery } from "./pastingSortingApi";
import useTranslator from "../../../hooks/useTranslator";
import { convertToUserTimezone, formatDateAndTime, getCurrentDate } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import usePermissions from "../../../hooks/usePermissions";

const PastingSorting = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filter, setFilter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});

  const { checkPermission } = usePermissions();
  const accessToEdit = checkPermission("pasting.edit_sorting", "update");

  const {
    data: sortingData,
    isLoading,
    isFetching,
  } = useGetPastingSortingQuery({ page, ...filter });

  const { data: summaryData } = { data: { data: {} } };

  const handleFilterApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setFilter(newFilters);
  };

  const handleAddRecord = () => {
    setModalMode("add");
    setSelectedRecord(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setModalMode(record.status === "moved" ? "view" : "edit");
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handlePageChange = (p) => setPage(p);

  const listItems = [
    {
      label: _t("total_seconds_kg", "Total Seconds KG"),
      value: summaryData?.data?.total_seconds_kg,
      icon: ColdStorageKg,
    },
    {
      label: _t("total_pieces_kg", "Total Pieces KG"),
      value: summaryData?.data?.total_pieces_kg,
      icon: KgEntered,
    },
    {
      label: _t("sorted_total_kg", "Sorted Total KG"),
      value: summaryData?.data?.sorted_total_kg,
      icon: ColdStorageKg,
    },
    {
      label: _t("sorted_total_bagged", "Sorted Total Bagged"),
      value: summaryData?.data?.sorted_total_bagged,
      icon: ColdStorageKg,
    },
    {
      label: _t("record_counting_moved", "Record Counting Moved"),
      value: summaryData?.data?.record_counting_moved,
      icon: KgEntered,
    },
  ];

  const columns = [
    {
      label: _t("date", "Date"),
      accessor: "entered_date_time",
      cell: ({ value }) => convertToUserTimezone(value),
    },
    { label: _t("supplier_name", "Supplier Name"), accessor: "supplier_name" },
    { label: _t("batch_code", "Batch Code"), accessor: "batch_code" },
    {
      label: _t("fruit_type", "Fruit Type"),
      accessor: "fruit_name",
    },
    {
      label: _t("fruit_type_variety", "Fruit Type Variety"),
      accessor: "fruit_type_variety",
      cell: ({ value }) =>
        value
          ? _t(value, value.charAt(0).toUpperCase() + value.slice(1))
          : "",
    },
    {
      label: _t("total_kg_entered", "Total KG Entered"),
      accessor: "total_entered_kg",
    },
    {
      label: _t("accepted_percent", "Accepted %"),
      accessor: "accepted_kg",
      cell: ({ row }) => {
        const total = parseFloat(row.total_entered_kg);
        const accepted = parseFloat(row.accepted_kg);
        if (!total || total === 0 || isNaN(accepted)) return "N/A";
        return `${((accepted / total) * 100).toFixed(2)}%`;
      },
    },
    {
      label: _t("rejected_percent", "Rejected %"),
      accessor: "rejected_kg",
      cell: ({ row }) => {
        const total = parseFloat(row.total_entered_kg);
        const rejected = parseFloat(row.rejected_kg);
        if (!total || total === 0 || isNaN(rejected)) return "N/A";
        return `${((rejected / total) * 100).toFixed(2)}%`;
      },
    },
    {
      label: _t("status", "Status"),
      accessor: "status",
      cell: ({ value }) => {
        let displayText = _t(value, value);
        if (value === "counting") displayText = _t("unloading", "Unloading");
        else if (value === "sorting") displayText = _t("sorting", "Sorting");
        else if (value === "moved") displayText = _t("moved", "Moved");

        return (
          <span className={value === "moved" ? "cmn_badge" : "cmn_badge inactive"}>
            {displayText}
          </span>
        );
      },
    },
    {
      label: _t("action", "Action"),
      accessor: "id",
      cell: ({ row }) => (
        <Button
          size="small"
          label={row.status === "moved" ? _t("view", "View") : _t("edit", "Edit")}
          className={row.status === "moved" ? "borderRedBtn" : ""}
          onClick={() => handleEdit(row)}
        />
      ),
    },
  ];

  return (
    <div>
      {showModal && (
        <PastingSortingModal
          show={showModal}
          setShow={setShowModal}
          mode={modalMode}
          record={selectedRecord}
        />
      )}

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("material_sorting_overview", "Material Sorting Overview")}
          </h6>
          <DateSelector setDate={setDate} />
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={
                currentDate === date
                  ? _t("today", "Today")
                  : formatDateAndTime(date, PROJECT_DATE_FORMAT)
              }
            />
          ))}
        </ul>
      </div>

      <Datatable
        title={_t("material_sorting_log", "Material Sorting Log")}
        columns={columns}
        tableData={sortingData?.data}
        buttons={[
          {
            label: _t("add_record", "Add Record"),
            onClick: handleAddRecord,
            icon: PlusSvg,
          },
        ]}
        pagination
        page={page}
        onPageChange={handlePageChange}
        showFilter={handleFilterApply}
        showFilterButton={true}
        filters={appliedFilters}
        isLoading={isLoading || isFetching}
      />
    </div>
  );
};

export default PastingSorting;
